import React, { useEffect, useState } from "react";
import { Card, TextArea, FormGroup } from "@blueprintjs/core";
import sprungbachImg from "./assets/Framing_1_Sprungbach.png";
import wellenhainImg from "./assets/Framing_1_Wellenhain.jpg";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";

type ScreenClick = {
    choice: number;
    millisecondsSinceFirstRender: number;
    reasoning?: string;
}

type ScreenResult = {
    selections: Array<ScreenClick>,
    explanation: string;
    usedHelp: boolean
}

export const FramingScreen3Result: ScreenResult = {
    selections: [],
    explanation: "",
    usedHelp: false
}

export const FramingScreen3 = (props: ScreenProps) => {
    const { t, language } = useLanguage();
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [explanation, setExplanation] = useState("");
    const { setCanContinue, result, setResult } = props;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
    }, [setResult]);

    // Defensive: always treat selections as an array
    const selections = Array.isArray(result?.selections) ? result.selections : [];
    const selected = selections.length > 0 ? selections[selections.length - 1].choice : -1;
    const hasReasoning = explanation.trim().length > 0;

    useEffect(() => {
        setCanContinue(selected !== -1 && hasReasoning);
    }, [selected, hasReasoning, setCanContinue]);

    const addSelection = (choice: number) => {
        setResult((prev: ScreenResult) => {
            const newSelections = Array.isArray(prev.selections) ? [...prev.selections] : [];
            newSelections.push({ choice, millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp });
            return { ...prev, selections: newSelections };
        });
    };

    const onExplanationChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setExplanation(event.target.value);
        setResult((prev: ScreenResult) => ({
            ...prev,
            explanation: event.target.value
        }));
    };

    const title = t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'title') ||
        (language === 'en' ? "Which Outing Will You Choose?" : "Welchen Ausflug wählst du?");

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <div style={{ ...commonStyles.card, maxWidth: 880, margin: "0 auto", padding: 36 }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 26px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center",
                    letterSpacing: 0.5
                }}>
                    {title}
                </h2>
                <div style={{
                    ...commonStyles.paragraph,
                    fontSize: 17,
                    color: colors.textDark,
                    margin: "0 0 18px 0",
                    textAlign: "justify"
                }}>
                    <p>{t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'content1')}</p>
                    <p>{t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'content2')}</p>
                </div>
                <div className="doublecard-container" style={{
                    display: 'flex',
                    gap: '28px',
                    justifyContent: 'center',
                    margin: '32px 0'
                }}>
                    <Card
                        className="doublecard"
                        interactive={true}
                        style={{
                            ...commonStyles.optionCard.default,
                            maxWidth: 370,
                            border: selected === 1
                                ? `2px solid ${colors.lichtBlau}`
                                : `1px solid ${colors.silverLight}`,
                            backgroundColor: selected === 1
                                ? colors.lichtBlauVeryLight
                                : colors.backgroundLight,
                            boxShadow: selected === 1
                                ? `0 0 14px ${colors.lichtBlauLighter}`
                                : "0 2px 10px rgba(0,0,0,0.08)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center"
                        }}
                        onClick={() => { addSelection(1) }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            marginBottom: 18,
                        }}>
                            <img
                                src={sprungbachImg}
                                alt={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'option3Title')}
                                style={{
                                    width: "100%",
                                    maxWidth: 290,
                                    height: 210,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
                                    display: "block"
                                }}
                            />
                        </div>
                        <h4 style={{
                            color: colors.brillantBlue,
                            fontSize: 20,
                            fontWeight: 600,
                            margin: "0 0 10px 0",
                            textAlign: "center"
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'option3Title')}
                        </h4>
                        <p style={{
                            color: colors.textDark,
                            fontSize: 17,
                            textAlign: "justify",
                            margin: 0,
                            lineHeight: 1.7
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'option3Desc')}
                        </p>
                    </Card>
                    <Card
                        className="doublecard"
                        interactive={true}
                        style={{
                            ...commonStyles.optionCard.default,
                            maxWidth: 370,
                            border: selected === 2
                                ? `2px solid ${colors.lichtBlau}`
                                : `1px solid ${colors.silverLight}`,
                            backgroundColor: selected === 2
                                ? colors.lichtBlauVeryLight
                                : colors.backgroundLight,
                            boxShadow: selected === 2
                                ? `0 0 14px ${colors.lichtBlauLighter}`
                                : "0 2px 10px rgba(0,0,0,0.08)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center"
                        }}
                        onClick={() => { addSelection(2) }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            marginBottom: 18,
                        }}>
                            <img
                                src={wellenhainImg}
                                alt={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'option4Title')}
                                style={{
                                    width: "100%",
                                    maxWidth: 290,
                                    height: 210,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
                                    display: "block"
                                }}
                            />
                        </div>
                        <h4 style={{
                            color: colors.brillantBlue,
                            fontSize: 20,
                            fontWeight: 600,
                            margin: "0 0 10px 0",
                            textAlign: "center"
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'option4Title')}
                        </h4>
                        <p style={{
                            color: colors.textDark,
                            fontSize: 17,
                            textAlign: "justify",
                            margin: 0,
                            lineHeight: 1.7
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'option4Desc')}
                        </p>
                    </Card>
                </div>
                <div style={{
                    fontSize: 17,
                    color: colors.textDark,
                    marginBottom: 10,
                    textAlign: "justify",
                    lineHeight: 1.7
                }}>
                    <p>{t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'choosePrompt')}</p>
                </div>
                <div className="reasoning-section" style={{ marginTop: 30 }}>
                    <FormGroup
                        label={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'reasonLabel')}
                        labelFor="reasoning-input"
                        labelInfo={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'reasonRequired')}
                    >
                        <TextArea
                            id="reasoning-input"
                            placeholder={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen3', 'reasonPlaceholder')}
                            value={explanation}
                            onChange={onExplanationChange}
                            fill={true}
                            autoResize={true}
                        />
                    </FormGroup>
                </div>
            </div>
        </div>
    );
};

export const FramingScreen3Config: ScreenConfig = {
    component: FramingScreen3,
    defaultResult: FramingScreen3Result,
    canContinueDefault: true
}